import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
    MapPin, Loader2, AlertTriangle, ExternalLink, RefreshCw, X, Thermometer, CircleGauge, Droplet, CalendarCheck, Search, CornerDownLeft
} from 'lucide-react';
// Импортируем вынесенные компоненты попапов
import Aksioma01PopupContent from './Map_Aksioma01Readings';
import Aksioma03PopupContent from './Map_Aksioma03Readings';
import Karat307PopupContent from './Map_Karat307Readings';

const API_URL = process.env.REACT_APP_API_URL;

const DEVICE_STYLES = {
    'Аксиома-01': { color: '#3b82f6', label: 'А-01' },
    'Аксиома-03': { color: '#a855f7', label: 'А-03' },
    'Карат-307': { color: '#22c55e', label: 'К-307' },
};

const geocodeAddress = (city, street, house) => {
    return new Promise((resolve) => {
        if (!window.ymaps || !window.ymaps.geocode) {
            resolve(null);
            return;
        }
        const query = [city, street, house].filter(Boolean).join(', ').trim();
        if (!query) {
            resolve(null);
            return;
        }
        window.ymaps.geocode(query, { results: 1 })
            .then((res) => {
                const first = res.geoObjects?.get(0);
                if (first) resolve(first.geometry.getCoordinates());
                else {
                    console.warn(`⚠️ Геокодер Яндекса не нашел адрес: "${query}"`);
                    resolve(null);
                }
            })
            .catch((err) => {
                console.error(`❌ Ошибка геокодинга для "${query}":`, err);
                resolve(null);
            });
    });
};

const saveCoordinatesToBackend = async (device, lat, lng) => {
    try {
        const token = localStorage.getItem('token');
        await axios.post(
            `${API_URL}/api/map/update-coordinates`,
            { deviceName: device.name, deviceType: device.deviceType, latitude: lat, longitude: lng },
            { headers: { Authorization: `Bearer ${token}` } }
        );
    } catch (err) {
        console.error(`❌ Не удалось сохранить координаты для ${device.name}:`, err);
    }
};

const getAddressKey = (d) => {
    return `${d.city || ''}|${d.street || ''}|${d.houseNumber || d.house || ''}`.toLowerCase().replace(/\s+/g, '');
};

const isToday = (dateString) => {
    if (!dateString) return false;
    const parts = String(dateString).split(/[.T\s-]/);
    let d;
    if (parts.length >= 3 && parts[0].length === 2 && parts[1].length === 2) {
        d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
    } else {
        d = new Date(dateString);
    }
    const today = new Date();
    return d.getDate() === today.getDate() &&
        d.getMonth() === today.getMonth() &&
        d.getFullYear() === today.getFullYear();
};

const extractMapParameter = (deviceType, readings, paramType) => {
    if (!readings) return { value: '—', date: null };
    let dateStr = '';
    let val = null;
    if (deviceType === 'Аксиома-01') {
        const t = readings.tekyshie || {};
        dateStr = t.date || '';
        if (paramType === 'temperature') val = t.t1;
        else if (paramType === 'pressure') val = t.P1;
        else if (paramType === 'flow') val = t.Go1;
    } else if (deviceType === 'Аксиома-03') {
        const t = readings.tekyshie || {};
        dateStr = t.onDateTime || '';
        if (paramType === 'temperature') val = t.temperatura?.[0];
        else if (paramType === 'pressure') val = t.davlenie?.[0];
        else if (paramType === 'flow') val = t.obemnui_rasxod?.[0];
    } else if (deviceType === 'Карат-307') {
        dateStr = readings.datetime || '';
        const vals = readings.parameters?.values || {};
        if (paramType === 'temperature') val = vals.T1;
        else if (paramType === 'pressure') val = vals.P1;
        else if (paramType === 'flow') val = vals.V1;
    }
    let formatted = '—';
    if (val !== null && val !== undefined && val !== -1 && val !== -100) {
        const num = parseFloat(val);
        if (!isNaN(num)) {
            if (paramType === 'temperature') formatted = num.toFixed(1) + '°';
            else if (paramType === 'pressure') formatted = num.toFixed(2);
            else if (paramType === 'flow') formatted = num.toFixed(2);
        }
    }
    return { value: formatted, date: dateStr };
};

const MapPage = () => {
    const mapRef = useRef(null);
    const ymapInstance = useRef(null);
    const markersRef = useRef([]);
    const customLayoutRef = useRef(null);
    const pendingNavigationDevice = useRef(null);
    const searchInputRef = useRef(null);
    const suggestionsRef = useRef(null);

    const [devices, setDevices] = useState([]);
    const [isLoadingDevices, setIsLoadingDevices] = useState(true);
    const [isMapReady, setIsMapReady] = useState(false);
    const [error, setError] = useState(null);
    const [selectedDevicesGroup, setSelectedDevicesGroup] = useState([]);
    const [activeDevice, setActiveDevice] = useState(null);
    const [showParamOnMap, setShowParamOnMap] = useState(false);
    const [selectedMapParam, setSelectedMapParam] = useState('temperature');
    const [showOnlyToday, setShowOnlyToday] = useState(true);
    const [mapReadingsCache, setMapReadingsCache] = useState({});
    const [isLoadingMapReadings, setIsLoadingMapReadings] = useState(false);

    // ✅ Поиск: состояния
    const [searchQuery, setSearchQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [targetSearchDevice, setTargetSearchDevice] = useState(null);

    const waitForMapReady = (map, callback) => {
        let retries = 0;
        const check = () => {
            if (!map || !map.container) return;
            try {
                const size = map.container.getSize();
                if (size && size[0] > 0 && size[1] > 0) {
                    callback();
                    return;
                }
            } catch (e) { /* ignore */ }
            if (retries < 15) {
                retries++;
                setTimeout(check, 100);
            } else {
                try { map.container.fitToViewport(); } catch (e) { }
                callback();
            }
        };
        check();
    };

    useEffect(() => {
        const storedDeviceStr = localStorage.getItem('selectedDeviceOnMap');
        if (storedDeviceStr) {
            try {
                const device = JSON.parse(storedDeviceStr);
                setActiveDevice(device);
                setSelectedDevicesGroup([device]);
                pendingNavigationDevice.current = device;
                localStorage.removeItem('selectedDeviceOnMap');
            } catch (e) {
                console.error('Ошибка парсинга устройства из localStorage:', e);
            }
        }
        const handler = (e) => {
            const device = e.detail?.device;
            if (device) {
                setActiveDevice(device);
                setSelectedDevicesGroup([device]);
                pendingNavigationDevice.current = device;
            }
        };
        window.addEventListener('selectDeviceOnMap', handler);
        return () => {
            window.removeEventListener('selectDeviceOnMap', handler);
            localStorage.removeItem('selectedDeviceOnMap');
        };
    }, []);

    useEffect(() => {
        const API_KEY = process.env.REACT_APP_YANDEX_MAPS_API_KEY || '';
        if (window.ymaps && window.ymaps.geocode) {
            setIsMapReady(true);
            return;
        }
        const script = document.createElement('script');
        script.src = `https://api-maps.yandex.ru/2.1/?apikey=${API_KEY}&lang=ru_RU`;
        script.async = true;
        script.onload = () => {
            window.ymaps.ready(() => setIsMapReady(true));
        };
        script.onerror = () => setError('Не удалось загрузить скрипт Яндекс.Карт');
        document.head.appendChild(script);
    }, []);

    useEffect(() => {
        if (!isMapReady || !mapRef.current || ymapInstance.current) return;
        const map = new window.ymaps.Map(mapRef.current, {
            center: [55.120953, 60.423439],
            zoom: 8,
            controls: ['zoomControl', 'fullscreenControl'],
        });
        ymapInstance.current = map;
    }, [isMapReady]);

    useEffect(() => {
        if (isMapReady && window.ymaps && !customLayoutRef.current) {
            customLayoutRef.current = window.ymaps.templateLayoutFactory.createClass(
                `<div style="position: relative; display: flex; flex-direction: column; align-items: center; cursor: pointer;">
                    <div style="
                        background: rgba(15, 23, 42, 0.95);
                        color: #ffffff;
                        padding: 2px 6px;
                        border-radius: 4px;
                        font-size: 11px;
                        font-weight: 600;
                        white-space: nowrap;
                        border: 1px solid $[options.iconColor];
                        box-shadow: 0 2px 4px rgba(0,0,0,0.4);
                        margin-bottom: 4px;
                        font-family: system-ui, -apple-system, sans-serif;
                    ">
                        $[properties.iconContent]
                    </div>
                    <div style="
                        width: 16px;
                        height: 16px;
                        border-radius: 50%;
                        background-color: $[options.iconColor];
                        border: 2px solid #ffffff;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.4);
                    "></div>
                </div>`
            );
        }
    }, [isMapReady]);

    const loadDevices = useCallback(async () => {
        setIsLoadingDevices(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/api/devices`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const list = res.data?.onServiceDevices || [];
            setDevices(list);
        } catch (err) {
            console.error('❌ Ошибка загрузки устройств:', err);
            setError('Не удалось загрузить список устройств');
        } finally {
            setIsLoadingDevices(false);
        }
    }, []);

    useEffect(() => {
        loadDevices();
    }, [loadDevices]);

    // ✅ Логика подсказок (Suggest)
    useEffect(() => {
        if (!searchQuery.trim()) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }
        const lowerQuery = searchQuery.toLowerCase().trim();
        const filtered = devices.filter(d =>
            (d.name && d.name.toLowerCase().includes(lowerQuery)) ||
            (d.description && d.description.toLowerCase().includes(lowerQuery)) ||
            (d.city && d.city.toLowerCase().includes(lowerQuery)) ||
            (d.street && d.street.toLowerCase().includes(lowerQuery)) ||
            ((d.houseNumber || d.house) && String(d.houseNumber || d.house).toLowerCase().includes(lowerQuery))
        );
        setSuggestions(filtered.slice(0, 8));
        setShowSuggestions(true);
    }, [searchQuery, devices]);

    // Закрытие подсказок при клике вне
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                searchInputRef.current && !searchInputRef.current.contains(event.target) &&
                suggestionsRef.current && !suggestionsRef.current.contains(event.target)
            ) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!showParamOnMap || devices.length === 0) {
            setMapReadingsCache({});
            return;
        }
        const fetchAllReadings = async () => {
            setIsLoadingMapReadings(true);
            const newCache = {};
            const token = localStorage.getItem('token');
            const chunkSize = 10;
            for (let i = 0; i < devices.length; i += chunkSize) {
                const chunk = devices.slice(i, i + chunkSize);
                const promises = chunk.map(async (device) => {
                    try {
                        let url = '';
                        if (device.deviceType === 'Аксиома-01') url = `${API_URL}/api/devices/aksioma01/${device.name}/readings`;
                        else if (device.deviceType === 'Аксиома-03') url = `${API_URL}/api/devices/aksioma03/${device.name}/readings`;
                        else if (device.deviceType === 'Карат-307') url = `${API_URL}/api/devices/karat307/${device.name}/readings/current`;

                        if (url) {
                            const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
                            newCache[device.name] = res.data;
                        }
                    } catch (err) {
                        newCache[device.name] = null;
                    }
                });
                await Promise.all(promises);
            }
            setMapReadingsCache(newCache);
            setIsLoadingMapReadings(false);
        };
        fetchAllReadings();
    }, [showParamOnMap, selectedMapParam, devices]);

    // ✅ Рендеринг маркеров и навигация
    useEffect(() => {
        if (!ymapInstance.current || devices.length === 0) return;

        markersRef.current.forEach((m) => ymapInstance.current.geoObjects.remove(m));
        markersRef.current = [];
        const bounds = [];

        (async () => {
            for (const device of devices) {
                const city = device.city || '';
                const street = device.street || '';
                const house = device.houseNumber || device.house || '';
                let coords = null;
                const lat = device.Latitude ?? device.latitude;
                const lng = device.Longitude ?? device.longitude;
                const hasDbCoords = lat != null && lng != null && !isNaN(parseFloat(lat)) && !isNaN(parseFloat(lng));

                if (hasDbCoords) {
                    coords = [parseFloat(lat), parseFloat(lng)];
                } else {
                    coords = await geocodeAddress(city, street, house);
                    if (coords) saveCoordinatesToBackend(device, coords[0], coords[1]);
                }

                if (!coords) continue;

                const style = DEVICE_STYLES[device.deviceType] || { color: '#64748b', label: '?' };
                const devicesAtAddress = devices.filter(d => getAddressKey(d) === getAddressKey(device));

                let hintContent = `${device.deviceType} ${device.name}`;
                let balloonContent;

                if (devicesAtAddress.length > 1) {
                    hintContent = `${city}, ${street}, д. ${house} (всего устройств: ${devicesAtAddress.length})`;
                    balloonContent = `${city}, ${street}, д. ${house}<br/><span style="color: #f59e0b; font-weight: bold; margin-top: 6px; display: inline-block;">Всего устройств на адресе: ${devicesAtAddress.length}</span>`;
                } else {
                    balloonContent = `<b>${device.deviceType} ${device.name}</b><br/>${city}, ${street}, д. ${house}`;
                }

                let markerOptions = { preset: 'islands#circleDotIcon', iconColor: style.color };
                let iconContent = '';

                if (showParamOnMap && customLayoutRef.current) {
                    const cached = mapReadingsCache[device.name];
                    let displayValue = '...';
                    if (cached) {
                        const { value, date } = extractMapParameter(device.deviceType, cached, selectedMapParam);
                        displayValue = value;
                        if (showOnlyToday && !isToday(date)) displayValue = '—';
                    }
                    markerOptions = {
                        iconLayout: customLayoutRef.current,
                        iconColor: style.color,
                        iconShape: { type: 'Circle', coordinates: [0, 0], radius: 15 }
                    };
                    iconContent = displayValue;
                }

                const marker = new window.ymaps.Placemark(
                    coords,
                    { hintContent, balloonContent, deviceName: device.name, deviceType: device.deviceType, iconContent },
                    markerOptions
                );

                marker.events.add('click', () => handleMarkerClick(device));
                ymapInstance.current.geoObjects.add(marker);
                markersRef.current.push(marker);
                bounds.push(coords);
            }

            // ✅ Логика навигации
            const navTarget = targetSearchDevice || pendingNavigationDevice.current;

            if (navTarget) {
                const targetDevice = devices.find(
                    d => d.name === navTarget.name && d.deviceType === navTarget.deviceType
                ) || navTarget;

                const lat = targetDevice.Latitude ?? targetDevice.latitude;
                const lng = targetDevice.Longitude ?? targetDevice.longitude;

                if (lat != null && lng != null && !isNaN(parseFloat(lat)) && !isNaN(parseFloat(lng))) {
                    const coords = [parseFloat(lat), parseFloat(lng)];

                    waitForMapReady(ymapInstance.current, () => {
                        try {
                            // ✅ ПЛАВНЫЙ ПЕРЕХОД С МАСШТАБИРОВАНИЕМ
                            // duration: 1000 - время анимации в мс (1 секунда)
                            // zoom: 17 - уровень приближения (достаточно близко для детального рассмотрения)
                            ymapInstance.current.setCenter(coords, 17, {
                                duration: 1000,
                                timingFunction: 'ease-in-out', // Плавное ускорение и замедление
                                checkZoomRange: true
                            });

                            // Действия после завершения анимации (через время > duration)
                            setTimeout(() => {
                                if (!ymapInstance.current) return;

                                // 1. Открываем балун над маркером
                                const targetMarker = markersRef.current.find(m =>
                                    m.properties.get('deviceName') === targetDevice.name &&
                                    m.properties.get('deviceType') === targetDevice.deviceType
                                );
                                if (targetMarker && targetMarker.balloon) {
                                    try {
                                        targetMarker.balloon.open();
                                    } catch (e) {
                                        console.warn("Не удалось открыть балун:", e);
                                    }
                                }

                                // 2. Открываем боковую карточку устройства
                                handleMarkerClick(targetDevice);

                            }, 1050); // Чуть больше чем duration, чтобы гарантировать завершение анимации

                        } catch (e) {
                            console.warn("Ошибка центрирования:", e);
                        }
                    });
                } else {
                    console.warn(`⚠️ Не найдены координаты для устройства: ${targetDevice.name}`);
                }

                // Сбрасываем цели навигации, чтобы анимация не повторялась при ре-рендере
                if (targetSearchDevice) setTargetSearchDevice(null);
                if (pendingNavigationDevice.current) pendingNavigationDevice.current = null;

            } else if (markersRef.current.length > 0 && !searchQuery) {
                // Стандартное поведение: показать все маркеры, если нет активного поиска/навигации
                waitForMapReady(ymapInstance.current, () => {
                    setTimeout(() => {
                        if (!ymapInstance.current) return;
                        const currentBounds = ymapInstance.current.geoObjects.getBounds();
                        if (currentBounds) {
                            try {
                                ymapInstance.current.setBounds(currentBounds, {
                                    checkZoomRange: true,
                                    zoomMargin: 60,
                                    useMapMargin: true,
                                    duration: 300 // Быстрая подстройка для общего вида
                                });
                            } catch (e) { console.warn("Ошибка установки bounds:", e); }
                        }
                    }, 150);
                });
            }
        })();
    }, [devices, isMapReady, showParamOnMap, selectedMapParam, showOnlyToday, mapReadingsCache, targetSearchDevice]);

    const fitAllMarkers = useCallback(() => {
        if (!ymapInstance.current) return;
        setSearchQuery('');
        setTargetSearchDevice(null);
        setShowSuggestions(false);

        waitForMapReady(ymapInstance.current, () => {
            const currentBounds = ymapInstance.current.geoObjects.getBounds();
            if (!currentBounds) return;
            try {
                ymapInstance.current.setBounds(currentBounds, {
                    checkZoomRange: true,
                    zoomMargin: 60,
                    useMapMargin: true,
                    duration: 300
                });
            } catch (e) { console.warn("Ошибка установки bounds:", e); }
        });
    }, []);

    const handleMarkerClick = (clickedDevice) => {
        const key = getAddressKey(clickedDevice);
        const group = devices.filter(d => getAddressKey(d) === key);
        setSelectedDevicesGroup(group);
        setActiveDevice(clickedDevice);
    };

    const handleClosePopup = () => {
        setSelectedDevicesGroup([]);
        setActiveDevice(null);
    };

    const handleGoToDevice = () => {
        if (!activeDevice) return;
        localStorage.setItem('switchPage', 'devices');
        localStorage.setItem('selectedDeviceFromMap', JSON.stringify(activeDevice));
        window.dispatchEvent(new CustomEvent('switchPageRequest'));
        setTimeout(() => {
            window.dispatchEvent(new CustomEvent('selectDeviceFromMap', { detail: { device: activeDevice } }));
        }, 100);
    };

    // ✅ ИЗМЕНЕНО: Обработчики поиска
    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (suggestions.length > 0) {
                const target = suggestions[0];
                setTargetSearchDevice(target);
                setSearchQuery(''); // Сброс поиска
                setShowSuggestions(false);
            } else if (searchQuery.trim()) {
                const lowerQuery = searchQuery.toLowerCase().trim();
                const firstMatch = devices.find(d =>
                    (d.name && d.name.toLowerCase().includes(lowerQuery)) ||
                    (d.city && d.city.toLowerCase().includes(lowerQuery))
                );
                if (firstMatch) {
                    setTargetSearchDevice(firstMatch);
                    setSearchQuery(''); // Сброс поиска
                    setShowSuggestions(false);
                }
            }
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
        }
    };

    // ✅ ИЗМЕНЕНО: Клик по подсказке
    const handleSuggestionClick = (device) => {
        setTargetSearchDevice(device);
        setSearchQuery(''); // Сброс поиска
        setShowSuggestions(false);
    };

    const isComplexDevice = activeDevice?.deviceType === 'Аксиома-03' || activeDevice?.deviceType === 'Карат-307';
    const popupWidth = isComplexDevice ? 'w-[450px]' : 'w-[340px]';

    return (
        <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
                    <MapPin className="w-6 h-6 sm:w-7 sm:h-7 text-blue-400" strokeWidth={2} />
                    Карта объектов учета
                </h2>
                <div className="flex gap-2">
                    <button
                        onClick={fitAllMarkers}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium transition flex items-center gap-2"
                        title="Показать все маркеры на карте"
                    >
                        <MapPin className="w-4 h-4" /> Показать все
                    </button>
                    <button onClick={loadDevices} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition flex items-center gap-2">
                        <RefreshCw className="w-4 h-4" /> Обновить
                    </button>
                </div>
            </div>

            {/* Панель управления: Поиск + Параметры */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-3 sm:p-4">
                <div className="flex flex-col lg:flex-row items-start lg:items-center gap-3 sm:gap-4">

                    {/* Блок поиска */}
                    <div className="relative w-full lg:max-w-md flex-shrink-0" ref={searchInputRef}>
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-4 w-4 text-slate-400" />
                        </div>
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Поиск устройства, адреса..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            onFocus={() => searchQuery && suggestions.length > 0 && setShowSuggestions(true)}
                            className="block w-full pl-10 pr-8 py-2 border border-slate-600 rounded-lg leading-5 bg-slate-900 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 sm:text-sm transition duration-150 ease-in-out"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => { setSearchQuery(''); setShowSuggestions(false); }}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500 hover:text-slate-300"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        )}

                        {/* Выпадающий список подсказок */}
                        {showSuggestions && suggestions.length > 0 && (
                            <div
                                ref={suggestionsRef}
                                className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-60 overflow-y-auto custom-scrollbar animate-in fade-in zoom-in-95 duration-100"
                            >
                                {suggestions.map((device, idx) => (
                                    <button
                                        key={`${device.name}-${idx}`}
                                        onClick={() => handleSuggestionClick(device)}
                                        className="w-full text-left px-4 py-2.5 hover:bg-slate-700 transition flex items-center gap-3 border-b border-slate-700/50 last:border-0"
                                    >
                                        <div
                                            className="w-2 h-2 rounded-full flex-shrink-0"
                                            style={{ backgroundColor: DEVICE_STYLES[device.deviceType]?.color || '#64748b' }}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-white truncate">
                                                {device.deviceType} <span className="text-blue-400">{device.name}</span>
                                            </div>
                                            <div className="text-xs text-slate-400 truncate">
                                                {[device.city, device.street, device.houseNumber || device.house].filter(Boolean).join(', ')}
                                            </div>
                                        </div>
                                        <CornerDownLeft className="w-3 h-3 text-slate-600 flex-shrink-0" />
                                    </button>
                                ))}
                                <div className="px-4 py-2 text-[10px] text-slate-500 text-center bg-slate-800/50">
                                    Нажмите Enter для перехода к первому результату
                                </div>
                            </div>
                        )}

                        {/* Подсказка "Ничего не найдено" */}
                        {showSuggestions && searchQuery && suggestions.length === 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl p-3 text-center text-sm text-slate-400">
                                Совпадений не найдено
                            </div>
                        )}
                    </div>

                    <div className="h-8 w-px bg-slate-700 hidden lg:block"></div>

                    <label className="flex items-center gap-2 cursor-pointer select-none whitespace-nowrap">
                        <input
                            type="checkbox"
                            checked={showParamOnMap}
                            onChange={(e) => setShowParamOnMap(e.target.checked)}
                            className="w-4 h-4 rounded border-slate-600 text-blue-600 focus:ring-blue-500 bg-slate-900"
                        />
                        <span className="text-sm font-medium text-white">Показывать параметры</span>
                    </label>

                    {showParamOnMap && (
                        <>
                            <div className="h-6 w-px bg-slate-600 hidden sm:block"></div>
                            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                                <div className="flex bg-slate-900 rounded-lg p-1 border border-slate-700">
                                    <button onClick={() => setSelectedMapParam('temperature')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition ${selectedMapParam === 'temperature' ? 'bg-red-500/20 text-red-400' : 'text-slate-400 hover:text-white'}`}>
                                        <Thermometer className="w-3.5 h-3.5" /> t1
                                    </button>
                                    <button onClick={() => setSelectedMapParam('pressure')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition ${selectedMapParam === 'pressure' ? 'bg-cyan-500/20 text-cyan-400' : 'text-slate-400 hover:text-white'}`}>
                                        <CircleGauge className="w-3.5 h-3.5" /> P1
                                    </button>
                                    <button onClick={() => setSelectedMapParam('flow')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition ${selectedMapParam === 'flow' ? 'bg-purple-500/20 text-purple-400' : 'text-slate-400 hover:text-white'}`}>
                                        <Droplet className="w-3.5 h-3.5" /> V1
                                    </button>
                                </div>

                                <label className="flex items-center gap-2 cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        checked={showOnlyToday}
                                        onChange={(e) => setShowOnlyToday(e.target.checked)}
                                        className="w-4 h-4 rounded border-slate-600 text-blue-600 focus:ring-blue-500 bg-slate-900"
                                    />
                                    <span className="text-xs sm:text-sm text-slate-300 flex items-center gap-1.5">
                                        <CalendarCheck className="w-3.5 h-3.5" /> Только сегодня
                                    </span>
                                </label>
                            </div>
                        </>
                    )}
                </div>

                {showParamOnMap && isLoadingMapReadings && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-blue-400">
                        <Loader2 className="animate-spin w-3.5 h-3.5" /> Загрузка показаний для маркеров...
                    </div>
                )}
            </div>

            {error && (
                <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
                </div>
            )}

            <div className="relative bg-slate-800 rounded-xl border border-slate-700 overflow-hidden" style={{ height: 'calc(100vh - 280px)', minHeight: '500px' }}>
                <div ref={mapRef} className="w-full h-full" />
                {!isMapReady && (
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80 z-20">
                        <div className="text-center">
                            <Loader2 className="animate-spin h-10 w-10 text-blue-500 mx-auto mb-3" />
                            <p className="text-slate-400 text-sm">Загрузка карты...</p>
                        </div>
                    </div>
                )}
                {isLoadingDevices && (
                    <div className="absolute top-4 left-4 bg-slate-800/90 border border-slate-700 rounded-lg px-3 py-2 flex items-center gap-2 text-sm text-slate-300 z-20">
                        <Loader2 className="animate-spin w-4 h-4 text-blue-500" /> Загрузка устройств...
                    </div>
                )}

                {activeDevice && (
                    <div className={`absolute top-4 right-4 ${popupWidth} max-w-[calc(100%-2rem)] max-h-[calc(100vh-2rem)] bg-slate-800 border border-slate-700 rounded-xl shadow-2xl z-30 flex flex-col`}>
                        <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-slate-700" style={{ borderLeft: `4px solid ${DEVICE_STYLES[activeDevice.deviceType]?.color || '#64748b'}` }}>
                            <div className="flex-1 min-w-0">
                                <div className="text-white font-semibold text-sm truncate">{activeDevice.deviceType} {activeDevice.name}</div>
                                <div className="text-slate-400 text-xs truncate">{activeDevice.city}, {activeDevice.street}, д. {activeDevice.houseNumber || activeDevice.house}</div>
                            </div>
                            <button onClick={handleClosePopup} className="text-slate-400 hover:text-white p-1 rounded hover:bg-slate-700">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        {selectedDevicesGroup.length > 1 && (
                            <div className="flex-shrink-0 px-4 py-2 bg-slate-800/80 border-b border-slate-700">
                                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">
                                    Устройства по этому адресу ({selectedDevicesGroup.length})
                                </label>
                                <select
                                    value={activeDevice.name}
                                    onChange={(e) => {
                                        const newDevice = selectedDevicesGroup.find(d => d.name === e.target.value);
                                        if (newDevice) setActiveDevice(newDevice);
                                    }}
                                    className="w-full px-2 py-1.5 bg-slate-900 border border-slate-600 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                    {selectedDevicesGroup.map(d => (
                                        <option key={d.name} value={d.name}>
                                            {d.deviceType} {d.name} {d.description ? `— ${d.description}` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div className="flex-1 overflow-y-auto custom-scrollbar p-4">
                            {activeDevice.deviceType === 'Аксиома-03' ? (
                                <Aksioma03PopupContent device={activeDevice} />
                            ) : activeDevice.deviceType === 'Карат-307' ? (
                                <Karat307PopupContent device={activeDevice} />
                            ) : (
                                <Aksioma01PopupContent device={activeDevice} />
                            )}
                        </div>
                        <div className="flex-shrink-0 px-4 py-3 border-t border-slate-700 bg-slate-900/50">
                            <button
                                onClick={handleGoToDevice}
                                className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition flex items-center justify-center gap-2"
                            >
                                <ExternalLink className="w-4 h-4" /> Перейти к устройству
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MapPage;